import { FC, useState } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Table } from '../../../components/table/index';
import { Company, useScreenSize, ValuationHooks } from '../../../hooks/index';
import { SharePlanType } from '../../../types/pool-plans.types';
import { VestingTask } from '../../../types/vesting-tasks.types';
import { ExerciseForm } from '../exercise-form/index';
import { PlanColumn, table, VestingPlanType } from './table-variant';
import {
  formatShortGrantId,
  VESTING_GRAPH_FULL_CHART_MIN_WIDTH_PX,
  VestingProgressBar,
} from './vesting-graph/index';
import { VestingTableItem } from './vesting-table-item';

type AutomaticVestingTableProps = {
  companyId: string;
  disableExercise: boolean;
  invalidateQuery: () => void;
  queriesEnabled: boolean;
  sharePlanType: SharePlanType;
  stakeholderId: string;
  vestingTasks: VestingTask[];
  vestingType: VestingPlanType;
};

export const AutomaticVestingTable: FC<AutomaticVestingTableProps> = ({
  companyId,
  disableExercise,
  invalidateQuery,
  queriesEnabled,
  sharePlanType,
  stakeholderId,
  vestingTasks,
  vestingType,
}) => {
  const [requestedSharePlanExercise, setSharePlanExercise] = useState<{
    id: string;
    type: SharePlanType;
  } | null>(null);

  const { width } = useScreenSize();
  const { valuation } = ValuationHooks.useCurrent({
    companyId,
    defaultErrorHandling: false,
    enabled: queriesEnabled,
  });
  const { issuedShares: companyIssuedShares } = Company.useIssuedShares({
    companyId,
    enabled: queriesEnabled,
  });

  const tableVestingTasks = vestingTasks.filter(
    (vestingTask) =>
      vestingTask.grantItem.plan.type === sharePlanType &&
      vestingTask.exercisable === (vestingType === VestingPlanType.EXERCISABLE),
  );

  if (tableVestingTasks.length === 0) return null;

  const columns = table[sharePlanType];
  const showFullGraphs = width >= VESTING_GRAPH_FULL_CHART_MIN_WIDTH_PX;

  return (
    <>
      {requestedSharePlanExercise && (
        <ExerciseForm
          companyId={companyId}
          handleClose={() => setSharePlanExercise(null)}
          invalidateQuery={invalidateQuery}
          isOpen={!!requestedSharePlanExercise}
          sharePlanId={requestedSharePlanExercise.id}
          type={requestedSharePlanExercise.type}
        />
      )}
      <div className="max flex w-full flex-col gap-3 rounded-lg bg-gray-50 p-1 !pt-3 max-lg:bg-transparent max-lg:p-0 lg:p-[2px]">
        <span className="hidden pl-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700 lg:block">
          {vestingType === VestingPlanType.AUTOMATIC ? (
            <>
              <AppFormattedMessage id="AUTOMATIC_VESTING" /> -
              <AppFormattedMessage id="VESTING_SCHEDULE_AND_PLAN" />
            </>
          ) : (
            <AppFormattedMessage id="VESTING_PLAN" />
          )}
        </span>

        <div className="flex gap-2 overflow-hidden bg-white px-2 max-lg:hidden lg:rounded-md lg:shadow-sm">
          <div className="flex h-full w-full flex-col gap-3 overflow-hidden rounded-md py-1">
            <span className="pl-2 pt-3 text-[10px] font-semibold uppercase leading-4 tracking-[1px] text-gray-500">
              <AppFormattedMessage id="ABOUT_PLAN" />
            </span>
            <div className="h-full w-full overflow-x-auto rounded-md border border-gray-100">
              <Table columnOrder={columns.planColumns} columnsTitle={columns.planColumnsTitle}>
                {tableVestingTasks.map((vestingTask) => (
                  <VestingTableItem
                    columns={columns.planColumns}
                    companyId={companyId}
                    companyIssuedShares={companyIssuedShares}
                    disableExercise={disableExercise}
                    key={vestingTask.id}
                    onRequestExercise={setSharePlanExercise}
                    stakeholderId={stakeholderId}
                    type="planInfo"
                    valuation={valuation}
                    vestingTask={vestingTask}
                  />
                ))}
              </Table>
            </div>
          </div>

          <div className="flex h-full w-full flex-col gap-3 overflow-hidden rounded-md py-1">
            <span className="pl-4 pt-3 text-[10px] font-semibold uppercase leading-4 tracking-[1px] text-gray-500">
              <AppFormattedMessage id="YOUR_VESTING_STRUCTURE" />
            </span>
            <div className="h-full w-full overflow-x-auto rounded-md border border-gray-100">
              <Table
                columnOrder={columns.vestingColumns}
                columnsTitle={columns.vestingColumnsTitle}
              >
                {tableVestingTasks.map((vestingTask) => (
                  <VestingTableItem
                    columns={columns.vestingColumns}
                    companyId={companyId}
                    companyIssuedShares={companyIssuedShares}
                    disableExercise={disableExercise}
                    key={vestingTask.id}
                    onRequestExercise={setSharePlanExercise}
                    stakeholderId={stakeholderId}
                    valuation={valuation}
                    vestingTask={vestingTask}
                  />
                ))}
              </Table>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 lg:hidden">
          <div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3">
            <span className="px-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
              {vestingType === VestingPlanType.AUTOMATIC ? (
                <AppFormattedMessage id="VESTING_SCHEDULE_AND_PLAN" />
              ) : (
                <AppFormattedMessage id="VESTING_PLAN" />
              )}
            </span>

            {tableVestingTasks.map((vestingTask) => (
              <div className="flex flex-col" key={vestingTask.id}>
                <span className="px-4 py-3 text-[10px] font-semibold uppercase leading-4 tracking-[1px] text-gray-500">
                  <AppFormattedMessage id="ABOUT_PLAN" />
                </span>
                <div className="flex flex-col gap-2 rounded-md bg-white p-3 shadow-sm">
                  <div className="flex flex-col overflow-hidden rounded-md bg-white shadow-sm">
                    <div className="flex h-[72px] items-center border-b border-gray-100 px-3 py-4">
                      <div className="flex flex-col text-gray-700">
                        <span className="text-base font-semibold leading-8">
                          {vestingTask.grantItem.plan.pool.name}
                        </span>
                        <span className="text-xs font-[450] leading-4">
                          {vestingTask.grantItem.plan.name}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col divide-y divide-gray-100">
                      <VestingTableItem
                        columns={columns.planColumns.filter(
                          (column) => column !== PlanColumn.PLAN && column !== PlanColumn.GRANT_ID,
                        )}
                        companyId={companyId}
                        companyIssuedShares={companyIssuedShares}
                        disableExercise={disableExercise}
                        mode="phoneView"
                        onRequestExercise={setSharePlanExercise}
                        sharePlanType={sharePlanType}
                        stakeholderId={stakeholderId}
                        type="planInfo"
                        valuation={valuation}
                        vestingTask={vestingTask}
                      />
                    </div>
                  </div>

                  <span className="px-1 py-3 text-[10px] font-semibold uppercase leading-4 tracking-[1px] text-gray-500">
                    <AppFormattedMessage id="YOUR_VESTING_STRUCTURE" />
                  </span>
                  <div className="flex flex-col divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-sm">
                    <VestingTableItem
                      columns={columns.vestingColumns}
                      companyId={companyId}
                      companyIssuedShares={companyIssuedShares}
                      disableExercise={disableExercise}
                      mode="phoneView"
                      onRequestExercise={setSharePlanExercise}
                      sharePlanType={sharePlanType}
                      stakeholderId={stakeholderId}
                      type="vestingInfo"
                      valuation={valuation}
                      vestingTask={vestingTask}
                    />
                  </div>

                  {!showFullGraphs && vestingTask.grantItem.plan.vesting && (
                    <VestingProgressBar
                      grantDate={vestingTask.grantItem.date}
                      grantId={formatShortGrantId(vestingTask.grantItem.id)}
                      totalShares={vestingTask.totalSharesCount}
                      vestedShares={vestingTask.vestedSharesCount}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
